import { useCallback, useMemo, useState } from "react";
import { getDoc } from "firebase/firestore";
import { pendingRef } from "./blogFirebase";

/**
 * Resolves the "before" side of a diff for a pending draft.
 *
 * Two distinct kinds of prior version exist and they resolve differently:
 *
 *  - REGENERATED drafts carry `replacedPrior`, the ID of the superseded doc in
 *    blog_posts_pending. Fetched lazily on first open.
 *  - REFRESHED drafts carry `refreshOf.previous`, an inline snapshot of the
 *    live WordPress post taken at generation time (see saveRefreshToFirestore).
 *    No fetch: the baseline must be what the model actually saw, and the live
 *    post may have moved on since.
 *
 * Drafts refreshed before the snapshot field existed have neither, so `hasPrior`
 * is false and the caller hides the diff affordance rather than showing an
 * empty panel.
 */
export function usePriorVersion(post) {
  const [showDiff, setShowDiff]       = useState(false);
  const [fetchedPrior, setFetched]    = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const snapshotPrior = post?.refreshOf?.previous || null;
  const replacedId    = post?.replacedPrior || null;

  const hasPrior = !!snapshotPrior || !!replacedId;

  const priorPost = useMemo(
    () => snapshotPrior || fetchedPrior,
    [snapshotPrior, fetchedPrior]
  );

  // A refresh diffs against the live post; a regeneration against the draft
  // it replaced. The heading has to say which, or the reviewer can't tell what
  // baseline they're reading.
  const diffLabel = snapshotPrior
    ? "Changes vs. the live published post"
    : "Changes from previous version";

  const toggleDiff = useCallback(async () => {
    // Snapshot is already in hand, and a re-open of an existing fetch is free.
    if (snapshotPrior || fetchedPrior) {
      setShowDiff(v => !v);
      return;
    }
    if (!replacedId) return;

    setDiffLoading(true);
    try {
      const snap = await getDoc(pendingRef(replacedId));
      if (snap.exists()) setFetched({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error("Failed to load prior version:", e);
    } finally {
      setDiffLoading(false);
      setShowDiff(true);
    }
  }, [snapshotPrior, fetchedPrior, replacedId]);

  return { showDiff, priorPost, hasPrior, diffLoading, diffLabel, toggleDiff };
}
